// @ts-expect-error untyped
import { makeAsyncComponent } from 'ContentEditorUI/components/shared/makeAsyncUtils';
import ModalDataTypes from 'ContentComponents/constants/ModalDataTypes';
import { MODAL_TYPES } from 'ContentEditorUI/lib/modalConstants';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @ts-expect-error untyped
import { getContent } from 'ContentEditorUI/redux/selectors/contentSchemaSelectors';
import { getSiteSettings } from 'ContentEditorUI/redux/selectors/siteSettingsSelectors';
import { createSelector } from 'reselect';
import { Map as ImmutableMap, fromJS } from 'immutable';
// @ts-expect-error untyped
import { closeModal } from 'ContentEditorUI/redux/actions/appActions';
import useContentModalActions from 'ContentEditorUI/hooks/useContentModalActions';
import redirectToNewUrl from 'ContentEditorUI/utils/contentManagementPanel/redirectToNewUrl';
import ContentRoutes from 'ContentUtils/Routes';
import { getHasAutotranslateAccess } from 'ContentEditorUI/redux/selectors/authSelectors';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { getUnifiedPublishType } from 'ContentEditorUI/redux/selectors/publishSelectors';
import { PublishTypes } from 'ContentEditorUI/constants/PublishTypes';
import i18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
const AsyncDeleteBlogPostModal = makeAsyncComponent(() => import( /* webpackChunkName: 'DeleteBlogPostModal' */'./DeleteBlogPostModal'));
const AsyncCloneBlogPostModal = makeAsyncComponent(() => import( /* webpackChunkName: 'CloneBlogPostModal' */'./CloneBlogPostModal'));
const AsyncCreateBlogTranslationExperimentModal = makeAsyncComponent(() => import( /* webpackChunkName: 'BlogCreateMlangVariantModal' */'ContentComponents/components/contentActionModals/TranslationModal').then(result => result.CreateBlogTranslationExperimentModal));
const AsyncCreateBlogPostModal = makeAsyncComponent(() => import( /* webpackChunkName: 'CreateBlogPostModal' */'./CreateBlogPostModal').then(module => module.CreateBlogPostModal));
const AsyncTranslateContentActionModal = makeAsyncComponent(() => import( /* webpackChunkName: 'TranslateContentActionModal' */'ContentEditorUI/components/modals/TranslateContentActionModal'));
const AsyncContentRemixModal = makeAsyncComponent(() => import( /* webpackChunkName: 'ContentRemixModal' */'ContentComponents/components/contentActionModals/ContentRemixModal').then(result => result.default));
const selectModalRelevantEntities = createSelector(getContent, content => {
  return ImmutableMap([
  // Translation modal expects parentBlog to be plain JS object.
  [content.id, fromJS(content).set('parentBlog', content.parentBlog)]]);
});
const getEditorUrlWithQueryParamsAfterCreatingTranslation = ({
  editUrl,
  newMlangVariantFromMasterLang
}) => {
  const queryParams = ['redirectFromMultiLangVariationCreation=true'];
  if (newMlangVariantFromMasterLang) {
    queryParams.push('newMlangVariantFromMasterLang=true');
  }
  return `${editUrl}?${queryParams.join('&')}`;
};
const ModalMap = {
  [MODAL_TYPES.CREATE_MLANG_VARIANT]: AsyncCreateBlogTranslationExperimentModal,
  [MODAL_TYPES.CONTENT_REMIX]: AsyncContentRemixModal,
  [MODAL_TYPES.CREATE_CONTENT_MODAL]: AsyncCreateBlogPostModal,
  [MODAL_TYPES.CLONE_CONTENT_MODAL]: AsyncCloneBlogPostModal,
  [MODAL_TYPES.DELETE_MODAL]: AsyncDeleteBlogPostModal,
  [MODAL_TYPES.TRANSLATE_CONTENT_ACTION_MODAL]: AsyncTranslateContentActionModal
};
const noop = () => {};
const useModalActions = () => {
  const dispatch = useDispatch();
  const onDeleteBlog = useCallback(() => {
    redirectToNewUrl(ContentRoutes.blogDashboard());
  }, []);
  const onCreateTranslationVariation = useCallback((content, opts) => {
    const {
      newMlangVariantFromMasterLang
    } = opts;
    const contentEditRoute = ContentRoutes.edit(content.id, `${content.categoryId}`);
    redirectToNewUrl(getEditorUrlWithQueryParamsAfterCreatingTranslation({
      editUrl: contentEditRoute,
      newMlangVariantFromMasterLang
    }));
  }, []);
  const onCreateTranslationVariationError = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);
  const {
    deleteContent,
    cloneContent,
    createTranslationVariation,
    createContent
  } = useContentModalActions(onDeleteBlog, noop, onCreateTranslationVariation, onCreateTranslationVariationError);
  const actions = useMemo(() => ({
    deleteContent: async blogPost => {
      await deleteContent(blogPost);
    },
    createContent: async blogId => {
      await createContent(blogId);
    },
    clone: async (blogPost, cloneName) => {
      await cloneContent(blogPost, cloneName);
    },
    closeModal: () => dispatch(closeModal()),
    createTranslationVariation: (blogsToTranslate, opts) => {
      const blog = blogsToTranslate.first().toJS();
      createTranslationVariation(blog, opts);
    }
  }), [deleteContent, cloneContent, createTranslationVariation, createContent, dispatch]);
  return actions;
};
const BlogPostOperationsModalSwitch = ({
  openModalName
}) => {
  const modalActions = useModalActions();
  const modalRelevantEntities = useSelector(selectModalRelevantEntities);
  const siteSettings = useSelector(getSiteSettings);
  const hasAutotranslateAccess = useSelector(getHasAutotranslateAccess);
  const unifiedPublishType = useSelector(getUnifiedPublishType);
  const isPublish = unifiedPublishType === PublishTypes.publish;
  let additionalProps = {};
  if (openModalName === MODAL_TYPES.CONTENT_REMIX && isPublish) {
    additionalProps = {
      embeddedPassthruProps: {
        remixHeaderText: i18n.text('contentRemixModal.title'),
        remixPanelDescription: i18n.text('contentRemixModal.description')
      }
    };
  }
  if (!openModalName || !ModalMap[openModalName]) {
    return null;
  }
  const BlogPostOperationsModal = ModalMap[openModalName];
  return /*#__PURE__*/_jsx(BlogPostOperationsModal, Object.assign({}, modalActions, {
    isBlogPost: true,
    modalRelevantEntities: modalRelevantEntities,
    managementDataType: 'blogPosts',
    modalDataType: ModalDataTypes.BLOG_POSTS,
    openModalName: openModalName,
    portalSettings: siteSettings,
    translationModalErrors: [],
    usageTracker: UsageTracker.tracker,
    portalHasAutotranslateAccess: hasAutotranslateAccess
  }, additionalProps));
};
export default BlogPostOperationsModalSwitch;